import { differenceInCalendarDays, parseISO } from "date-fns";
import {
  GoalPaceStatus,
  InvestmentGoalResponse,
  SavingsGoalResponse,
} from "../../../services";
import {
  DateFormat,
  formatDate,
  predictedDateFromMonths,
} from "../../../utils";
import { pastGoalsPath, rollForwardPath } from "../../Settings/constants";
import {
  hasCurrentValuation,
  isSettledInvestmentGoal,
  settledAtOf,
} from "../../Settings/GoalsSection/investmentGoalStatus";
import { compactEuro, investmentPlanGoal, savingsPlanGoal } from "./planGoals";
import {
  JustEndedGoal,
  PlanGoalsInputs,
  PlanGoalListInputs,
  PlanListGoal,
} from "./types";

export const JUST_ENDED_DAYS = 30;

const RANK = {
  behind: 0,
  unreachable: 1,
  onTrack: 2,
  projected: 3,
  ahead: 4,
  reached: 5,
} as const;

const PACE_RANK: Record<GoalPaceStatus, number> = {
  BEHIND: RANK.behind,
  ON_TRACK: RANK.onTrack,
  AHEAD: RANK.ahead,
};

const savingsRankOf = ({ progress }: SavingsGoalResponse) =>
  progress.lifecycleStatus === "COMPLETED"
    ? RANK.reached
    : PACE_RANK[progress.paceStatus];

const investmentRankOf = (goal: InvestmentGoalResponse) => {
  const { projection } = goal;
  if (!hasCurrentValuation(goal)) {
    return RANK.unreachable;
  }
  if (projection.reached) {
    return RANK.reached;
  }
  return predictedDateFromMonths(projection.monthsToTarget) === null
    ? RANK.unreachable
    : RANK.projected;
};

const isWithinJustEndedWindow = (endedAt: string, today: Date) => {
  const daysAgo = differenceInCalendarDays(today, parseISO(endedAt));
  return daysAgo >= 0 && daysAgo <= JUST_ENDED_DAYS;
};

const canRollForward = (
  goal: SavingsGoalResponse,
  savingsGoals: SavingsGoalResponse[],
  today: Date,
) =>
  goal.closedAt !== null &&
  isWithinJustEndedWindow(goal.closedAt, today) &&
  !savingsGoals.some(({ previousGoalId }) => previousGoalId === goal.id);

export const planGoalsOf = ({
  savingsGoals,
  investmentGoals,
}: PlanGoalListInputs): PlanListGoal[] => {
  const savingsRows = savingsGoals
    .filter((goal) => goal.closedAt === null)
    .map((goal) => ({
      rank: savingsRankOf(goal),
      row: {
        key: `savings-${goal.id}`,
        goal: savingsPlanGoal(goal, false),
        subtitle: `${compactEuro(goal.targetAmount)} by ${formatDate(goal.targetDate, DateFormat.MY_SPACE)}`,
      },
    }));
  const investmentRows = investmentGoals
    .filter((goal) => !isSettledInvestmentGoal(goal))
    .map((goal) => ({
      rank: investmentRankOf(goal),
      row: {
        key: `investment-${goal.id}`,
        goal: investmentPlanGoal(goal),
        subtitle: `${compactEuro(goal.targetAmount)} target`,
      },
    }));

  return [...savingsRows, ...investmentRows]
    .sort(
      (a, b) =>
        a.rank - b.rank || a.row.goal.name.localeCompare(b.row.goal.name),
    )
    .map(({ row }) => row);
};

export const justEndedGoalsOf = ({
  savingsGoals,
  investmentGoals,
  today,
}: PlanGoalsInputs): JustEndedGoal[] => {
  const savings = savingsGoals
    .filter((goal) => canRollForward(goal, savingsGoals, today))
    .map((goal): JustEndedGoal => ({
      key: `savings-${goal.id}`,
      goal: savingsPlanGoal(goal, false),
      linkHref: rollForwardPath(goal.id),
      linkLabel: "Roll forward →",
    }));
  const investments = investmentGoals
    .filter((goal) => {
      const settledAt = settledAtOf(goal);
      return settledAt !== null && isWithinJustEndedWindow(settledAt, today);
    })
    .map((goal): JustEndedGoal => ({
      key: `investment-${goal.id}`,
      goal: investmentPlanGoal(goal),
      linkHref: pastGoalsPath,
      linkLabel: "Review →",
    }));

  return [...savings, ...investments];
};

export const pastGoalCountOf = ({
  savingsGoals,
  investmentGoals,
}: PlanGoalListInputs) =>
  savingsGoals.filter((goal) => goal.closedAt !== null).length +
  investmentGoals.filter(isSettledInvestmentGoal).length;
